import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, Loader2, Send, X, Zap } from "lucide-react";
import { errorFrom } from "@/lib/errors";
import { emailTemplatePreview, emailTemplates, type EmailTemplateCode, type EmailTemplateInfo } from "@/lib/emailApi";

interface Props {
  onSendNow: (tpl: EmailTemplateInfo) => void;
}

// OFFICIAL EMAILS: 3 fixed server-side templates. "Anteprima" renders a sample; "Invia ora" hands the template to the composer
// (recipient -> preview -> confirm -> send). Nothing is sent from here.
export function OfficialEmails({ onSendNow }: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<EmailTemplateInfo[]>([]);
  const [preview, setPreview] = useState<{ code: EmailTemplateCode; subject: string; html: string; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState<EmailTemplateCode | null>(null);

  useEffect(() => { emailTemplates().then(setItems).catch(() => undefined); }, []);

  const openPreview = async (code: EmailTemplateCode) => {
    setBusy(code);
    try { setPreview(await emailTemplatePreview(code)); } catch (err) { toast.error(errorFrom(err)); } finally { setBusy(null); }
  };

  return (
    <section className="glass-panel rounded-2xl p-6" data-testid="admin-email-official">
      <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-cyan-300">{t("adminEmail.official.eyebrow")}</p>
      <h2 className="mt-1 text-lg font-bold text-white">{t("adminEmail.official.title")}</h2>
      <div className="mt-5 grid gap-5 md:grid-cols-3">
        {items.map((tpl) => (
          <article key={tpl.code} data-testid={`admin-email-template-${tpl.code}`} className="flex flex-col overflow-hidden rounded-2xl border border-cyan-300/25 bg-black/30 shadow-[0_0_24px_rgba(34,211,238,0.08)]">
            <img src={tpl.hero_url} alt={tpl.name} className="aspect-[3/4] w-full object-cover object-top" loading="lazy" data-testid={`admin-email-template-hero-${tpl.code}`} />
            <div className="flex flex-1 flex-col gap-2 p-4">
              <h3 className="text-base font-bold text-white">{tpl.name}</h3>
              <p className="text-xs leading-relaxed text-muted-foreground">{tpl.description}</p>
              <p className="text-[11px] text-muted-foreground"><span className="uppercase tracking-widest">{t("adminEmail.subjectShort")}:</span> <span className="text-white/80">{tpl.subject}</span></p>
              <span data-testid={`admin-email-template-automation-${tpl.code}`}
                className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${tpl.automation === "AUTO_SIGNUP" ? "border-lime-300/50 text-lime-200" : "border-white/15 text-muted-foreground"}`}>
                <Zap className="h-3 w-3" />{t(`adminEmail.official.automation.${tpl.automation}`)}
              </span>
              {!tpl.cta_url && <p className="text-[11px] text-yellow-200" data-testid={`admin-email-template-ctamissing-${tpl.code}`}>{t("adminEmail.official.ctaMissing")}</p>}
              <div className="mt-auto flex gap-2 pt-3">
                <button type="button" onClick={() => openPreview(tpl.code)} disabled={busy === tpl.code} data-testid={`admin-email-template-preview-${tpl.code}`}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/20 px-3 py-2 text-xs font-semibold text-white hover:border-cyan-300/60">
                  {busy === tpl.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}{t("adminEmail.official.preview")}
                </button>
                <button type="button" onClick={() => onSendNow(tpl)} data-testid={`admin-email-template-send-${tpl.code}`}
                  className="cta-hero inline-flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs">
                  <Send className="h-3.5 w-3.5" />{t("adminEmail.official.sendNow")}
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" data-testid="admin-email-template-preview-modal">
          <div className="relative w-full max-w-3xl rounded-2xl border border-cyan-300/30 bg-[#0b1526] p-4">
            <button type="button" onClick={() => setPreview(null)} data-testid="admin-email-template-preview-close" className="absolute right-3 top-3 rounded-full border border-white/15 p-1.5 text-white hover:border-white/50"><X className="h-4 w-4" /></button>
            <p className="pr-10 text-sm font-semibold text-white">{preview.subject}</p>
            {preview.warnings.includes("CTA_URL_MISSING") && <p className="mt-1 text-xs text-yellow-200">{t("adminEmail.official.ctaMissing")}</p>}
            <iframe title="official email preview" srcDoc={preview.html} sandbox="allow-same-origin" className="mt-3 h-[70vh] w-full rounded-xl border border-white/10 bg-[#050b16]" data-testid="admin-email-template-preview-frame" />
          </div>
        </div>
      )}
    </section>
  );
}
